import React, { useState } from 'react';
import { FiX, FiDownload, FiGrid, FiSettings, FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import axios from 'axios';
import QRCustomizer from './QRCustomizer';

interface QRModalProps {
  url: any;
  isOpen: boolean;
  onClose: () => void;
}

const QRModal: React.FC<QRModalProps> = ({ url, isOpen, onClose }) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrCode, setQrCode] = useState(url?.qrCode || '');
  const [downloadFormat, setDownloadFormat] = useState<'png' | 'svg' | 'pdf'>('png');
  const [showCustomizer, setShowCustomizer] = useState(false);
  const isPro = user?.tier === 'pro';

  if (!isOpen || !url) return null;

  const handleRegenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await api.post(`/urls/${url.hash}/qr/regenerate`);
      setQrCode(response.data.qrCode);
      toast.success('QR code regenerated');
    } catch (error) {
      toast.error('Failed to regenerate QR code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!qrCode && !url.qrCode) {
      toast.error('No QR code available');
      return;
    }

    if (downloadFormat === 'png') {
      const link = document.createElement('a');
      link.href = qrCode || url.qrCode;
      link.download = `qr-${url.hash}.png`;
      link.click();
      toast.success('QR code downloaded');
    } else {
      try {
        // Use axios directly for blob download
        const response = await axios.get(
          `/api/urls/${url.hash}/qr/download?format=${downloadFormat}`,
          {
            responseType: 'blob',
            withCredentials: true,
          }
        );
        
        const blob = new Blob([response.data]);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `qr-${url.hash}.${downloadFormat}`;
        link.click();
        toast.success(`QR code downloaded as ${downloadFormat.toUpperCase()}`);
      } catch (error) {
        toast.error(`Failed to download as ${downloadFormat.toUpperCase()}`);
      }
    }
  };

  const handleCustomize = () => {
    if (!isPro) {
      toast.error('QR customization is available for Pro users');
      return;
    }
    setShowCustomizer(true);
  };

  const handleCustomizationComplete = (newQrCode: string) => {
    setQrCode(newQrCode);
    setShowCustomizer(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            {showCustomizer && (
              <button
                onClick={() => setShowCustomizer(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h3 className="text-xl font-bold text-gray-900">
              {showCustomizer ? 'Customize QR Code' : 'QR Code'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {showCustomizer ? (
          <QRCustomizer
            url={url}
            onQRGenerated={handleCustomizationComplete}
            onClose={() => setShowCustomizer(false)}
          />
        ) : (
          <>
            {/* QR Code Display */}
            <div className="bg-gray-50 rounded-lg p-8 mb-4 flex items-center justify-center">
          {qrCode || url.qrCode ? (
            <img
              src={qrCode || url.qrCode}
              alt="QR Code"
              className="w-64 h-64"
            />
          ) : (
            <div className="text-center">
              <FiGrid className="w-16 h-16 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No QR code generated</p>
              <button
                onClick={handleRegenerate}
                disabled={isGenerating}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {isGenerating ? 'Generating...' : 'Generate QR Code'}
              </button>
            </div>
          )}
        </div>

        {/* URL Info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Short URL</p>
          <p className="font-mono text-sm text-primary-600 break-all">{url.shortUrl}</p>
        </div>

        {/* Download Options */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Download Format
          </label>
          <div className="flex space-x-2">
            <button
              onClick={() => setDownloadFormat('png')}
              className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                downloadFormat === 'png'
                  ? 'bg-primary-50 border-primary-600 text-primary-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              PNG
            </button>
            <button
              onClick={() => {}}
              className="flex-1 px-3 py-2 rounded-lg border bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
              disabled={true}
              title="Coming soon"
            >
              SVG (Soon)
            </button>
            <button
              onClick={() => {}}
              className="flex-1 px-3 py-2 rounded-lg border bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
              disabled={true}
              title="Coming soon"
            >
              PDF (Soon)
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleDownload}
            disabled={!qrCode && !url.qrCode}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="w-4 h-4" />
            <span>Download</span>
          </button>
          <button
            onClick={handleCustomize}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              isPro
                ? 'bg-gradient-to-r from-purple-600 to-primary-600 text-white hover:from-purple-700 hover:to-primary-700'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!isPro}
          >
            <FiSettings className="w-4 h-4" />
            <span>Customize {!isPro && '(Pro)'}</span>
          </button>
        </div>

            {!isPro && (
              <p className="text-xs text-center text-gray-500 mt-3">
                Upgrade to Pro for QR customization
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QRModal;